import { client as apolloClient } from '@xbcb/apollo-client';
import { executeMutation } from './executeMutation';
import {
  updateWorkOrderStatus,
  updateWorkOrderTaskStatus,
} from './sharedQueries';
import { WorkOrderStatus, WorkOrderTaskStatus } from '@xbcb/work-order-types';
import { message } from 'antd';
import {
  ShipmentCommercialInvoiceDetailStatus,
  ShipmentFilingSessionPhaseName,
  ShipmentFilingSessionStepName,
  ShipmentFilingSessionStepStatus,
  type ShipmentFilingSessionPhase,
  type ShipmentFilingSessionStep,
} from '__generated__/graphql';
import { getRecordPath } from './getRecordPath';
import { getRecordType } from '@xbcb/core';
import { RecordType } from '@xbcb/shared-types';
import {
  GET_WORK_ORDER_TASK,
  SEARCH_SHIPMENTS,
  SEARCH_SHIPMENT_FILING_SESSIONS,
  UPDATE_SHCID,
  UPDATE_SHIPMENT_FILING_SESSION,
} from './handleRejectTask.query';

interface HandleRejectTaskParams {
  bookingId: string;
  workOrderId: string;
  workOrderVersion: number;
  workOrderTaskId?: string;
  push: (path: string) => void;
}

export const handleRejectTask = async ({
  bookingId,
  workOrderId,
  workOrderVersion,
  workOrderTaskId,
  push,
}: HandleRejectTaskParams): Promise<void> => {
  try {
    const { data: shipmentData } = await apolloClient.query({
      query: SEARCH_SHIPMENTS,
      variables: {
        input: {
          searchCriteria: {
            bookingId: {
              operator: 'EQUALS',
              values: [bookingId],
            },
          },
        },
      },
      fetchPolicy: 'network-only',
    });

    const shipment = shipmentData?.searchShipments?.results?.[0];
    const shcid = shipment?.legs?.[0]?.commercialInvoiceDetail;

    if (!shcid?.id) {
      throw new Error(
        'ShipmentCommercialInvoiceDetail not found for this booking',
      );
    }

    // Rejecting the commercial invoice and looking up the shipment's filing
    // session are independent (both only need the SEARCH_SHIPMENTS result), so
    // run them concurrently to avoid a request waterfall.
    const [, filingSessionResult] = await Promise.all([
      executeMutation({
        mutation: UPDATE_SHCID,
        variables: {
          id: shcid.id,
          version: shcid.version,
          input: {
            status: ShipmentCommercialInvoiceDetailStatus.REJECTED,
          },
        },
        successMessage: 'Commercial invoice rejected successfully',
      }),
      shipment?.id
        ? apolloClient.query({
            query: SEARCH_SHIPMENT_FILING_SESSIONS,
            variables: {
              input: {
                searchCriteria: {
                  shipmentId: {
                    operator: 'EQUALS',
                    values: [shipment.id],
                  },
                },
              },
            },
            fetchPolicy: 'network-only',
          })
        : Promise.resolve({ data: null }),
    ]);

    // Update the SFS to unblock the shipper to edit and re-submit the filing.
    const filingSession =
      filingSessionResult?.data?.searchShipmentFilingSessions?.results?.[0];

    const stepUpdates =
      filingSession?.phases
        ?.filter(
          (phase: ShipmentFilingSessionPhase) =>
            phase?.name !== ShipmentFilingSessionPhaseName.DOCUMENT_PROCESSING,
        )
        .flatMap((phase: ShipmentFilingSessionPhase) =>
          (phase?.steps ?? []).map((step: ShipmentFilingSessionStep) => ({
            phaseName: phase.name,
            stepName: step.name,
            // In Product Compliance the Document Upload step must be re-actioned
            // by the shipper; every other step in the phase is marked complete.
            status:
              phase.name ===
                ShipmentFilingSessionPhaseName.PRODUCT_COMPLIANCE &&
              step.name === ShipmentFilingSessionStepName.DOCUMENT_UPLOAD
                ? ShipmentFilingSessionStepStatus.ACTION_REQUIRED
                : ShipmentFilingSessionStepStatus.COMPLETED,
          })),
        ) ?? [];

    if (filingSession?.id && stepUpdates.length > 0) {
      await executeMutation({
        mutation: UPDATE_SHIPMENT_FILING_SESSION,
        variables: {
          id: filingSession.id,
          version: filingSession.version,
          input: { stepUpdates },
        },
      });
    }

    await executeMutation({
      mutation: updateWorkOrderStatus,
      variables: {
        id: workOrderId,
        version: workOrderVersion,
        input: {
          status: WorkOrderStatus.BLOCKED,
        },
      },
    });

    if (workOrderTaskId) {
      const { data: taskData } = await apolloClient.query({
        query: GET_WORK_ORDER_TASK,
        variables: { id: workOrderTaskId },
        fetchPolicy: 'network-only',
      });
      const latestTaskVersion = taskData?.workOrderTask?.version;

      await executeMutation({
        mutation: updateWorkOrderTaskStatus,
        variables: {
          id: workOrderTaskId,
          version: latestTaskVersion,
          input: {
            status: WorkOrderTaskStatus.BLOCKED,
            tags: [
              { key: 'BLOCKED_REASON', value: 'SELLER_DOCUMENTS_REJECTED' },
            ],
          },
        },
      });
    }

    const recordType = getRecordType(workOrderId) as RecordType;
    const recordPath = getRecordPath({ recordType, id: workOrderId });
    push(`${recordPath}?tab=Support Cases`);
  } catch (error) {
    console.error('Failed to reject task:', error);
    void message.error(
      `Failed to reject task: ${error.message || 'Unknown error'}`,
      5.0,
    );
    throw error;
  }
};
